import { useCallback, useMemo, useState } from 'react';
import { AlertCircle, ArrowDown, ArrowUp, ArrowUpDown, History as HistoryIcon, Pencil, SquareStack } from 'lucide-react';
import { clsx } from 'clsx';
import type { FieldMeta, ResourceMeta } from '../../types/resource';
import type { SubmissionListParams, SubmissionRow } from '../../types/submission';
import { useResourceMeta } from '../../hooks/useResourceMeta';
import { useSubmissions } from '../../hooks/useSubmissions';
import { formatFieldValue } from '../../utils/fieldValue';
import { ResourceEditDialog } from '../ResourceEditDialog/ResourceEditDialog';
import { ResourceHistoryDrawer } from '../ResourceHistoryDrawer/ResourceHistoryDrawer';
// Circular import: ResourceDetailDrawer → ChildrenPanel →
// ChildResourceTable → ResourceDetailDrawer. Safe at runtime because
// every hop only references its counterpart from inside JSX (render
// time), not at module init. This lets a nested "view sub-records"
// click open another drawer on top of the current one, matching
// arbitrary-depth `children[]` chains in the meta.
import { ResourceDetailDrawer } from './ResourceDetailDrawer';

interface ChildResourceTableProps {
  /** The child resource's own name (used in every downstream URL). */
  childResource: string;
  /** The parent's resource name — passed as `parent_resource` filter. */
  parentResource: string;
  /** The parent row's id — passed as `parent_id` filter. */
  parentRowId: number | string;
}

const DEFAULT_PAGE_SIZE = 10;

/**
 * A self-contained mini-grid for one child resource under a parent
 * row. Fetches its own meta + submissions (server filters by
 * `parent_resource` + `parent_id`), and manages its own Edit /
 * History / View-sub-records dialogs so the reviewer can operate
 * on child rows without leaving the parent view.
 *
 * Nested drill-down: if this child's meta declares its own
 * `children[]`, every row gets a "View sub-records" action that
 * opens *another* `ResourceDetailDrawer` on top of the current one.
 * Stacked drawers keep each level's content in its own bounded
 * viewport instead of the older inline-expansion pattern that made
 * every deeper level fight for horizontal room inside the parent
 * grid's already-wide table.
 *
 * Kept intentionally simpler than the top-level `ResourceGrid`:
 *
 *   - No filter bar. Child rows are already narrowed to a single
 *     parent — additional filters would rarely help.
 *   - No card view. Children are always tabular in this context.
 */
export function ChildResourceTable({
  childResource,
  parentResource,
  parentRowId,
}: ChildResourceTableProps) {
  const { meta, loading: metaLoading, error: metaError } = useResourceMeta(childResource);

  const [sortState, setSortState] = useState<{ column: string; order: 'asc' | 'desc' } | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  const [editingRow, setEditingRow] = useState<SubmissionRow | null>(null);
  const [historyRow, setHistoryRow] = useState<SubmissionRow | null>(null);
  // View-sub-records opens the same `ResourceDetailDrawer` used at
  // the top level. Stacked drawers give each level its own bounded
  // viewport instead of nesting a wider table inside a wider table.
  const [detailRow, setDetailRow] = useState<SubmissionRow | null>(null);

  const listParams = useMemo<SubmissionListParams>(
    () => ({
      parent_resource: parentResource,
      parent_id: parentRowId,
      sort: sortState?.column,
      sort_order: sortState?.order,
      page,
      page_size: pageSize,
    }),
    [parentResource, parentRowId, sortState, page, pageSize],
  );

  const {
    page: submissions,
    loading: listLoading,
    error: listError,
    refetch: refetchList,
  } = useSubmissions(meta ? childResource : undefined, listParams);

  const handleSort = useCallback((column: string) => {
    setPage(1);
    setSortState((current) => {
      if (current?.column === column) {
        return { column, order: current.order === 'asc' ? 'desc' : 'asc' };
      }
      return { column, order: 'asc' };
    });
  }, []);

  if (metaLoading && !meta) {
    return <InlineNotice message="Loading child resource…" />;
  }
  if (metaError) {
    return (
      <ErrorNotice
        title="Couldn't load child resource"
        message={metaError.message}
      />
    );
  }
  if (!meta) {
    return null;
  }

  const columns = visibleColumns(meta);
  const sortable = sortableColumnTitles(meta);
  const rows = submissions?.data ?? [];
  const totalColumns = columns.length + 1;
  const totalPages =
    typeof submissions?.total === 'number'
      ? Math.max(1, Math.ceil(submissions.total / pageSize))
      : undefined;
  const hasPrev = page > 1;
  const hasNext =
    submissions?.has_next_page === true
      ? true
      : submissions?.has_next_page === false
        ? false
        : totalPages !== undefined
          ? page < totalPages
          : rows.length >= pageSize;

  return (
    <div className="rounded-lg border border-rgf-gray-200 bg-white shadow-sm">
      {listError && (
        <div className="m-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-900">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {listError.message}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-rgf-gray-200 text-sm">
          <thead className="bg-rgf-gray-50">
            <tr>
              {columns.map((col) => {
                const isSortable = sortable.has(col.title);
                const isActive = sortState?.column === col.title;
                return (
                  <th
                    key={col.title}
                    scope="col"
                    className="whitespace-nowrap px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-rgf-gray-600"
                  >
                    {isSortable ? (
                      <button
                        type="button"
                        onClick={() => handleSort(col.title)}
                        className={clsx(
                          'inline-flex items-center gap-1 rounded hover:text-rgf-gray-900',
                          isActive && 'text-rgf-gray-900',
                        )}
                      >
                        <span>{col.display_title || col.title}</span>
                        {!isActive ? (
                          <ArrowUpDown className="h-3 w-3 opacity-60" />
                        ) : sortState?.order === 'desc' ? (
                          <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUp className="h-3 w-3" />
                        )}
                      </button>
                    ) : (
                      <span>{col.display_title || col.title}</span>
                    )}
                  </th>
                );
              })}
              <th
                scope="col"
                className="w-20 whitespace-nowrap px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wider text-rgf-gray-600"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rgf-gray-100 bg-white">
            {listLoading && rows.length === 0 && (
              <>
                {[0, 1, 2].map((r) => (
                  <tr key={r}>
                    {Array.from({ length: totalColumns }).map((_, c) => (
                      <td key={c} className="px-3 py-2">
                        <div className="h-2.5 w-3/4 animate-pulse rounded bg-rgf-gray-200" />
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            )}
            {!listLoading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={totalColumns}
                  className="px-3 py-6 text-center text-xs text-rgf-gray-500"
                >
                  No child records yet.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={String(row.id ?? Math.random())} className="hover:bg-rgf-gray-50">
                {columns.map((col) => (
                  <td key={col.title} className="whitespace-nowrap px-3 py-2 text-rgf-gray-800">
                    {formatFieldValue(col, row[col.title])}
                  </td>
                ))}
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setDetailRow(row)}
                      className="rounded p-1 text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-primary-700"
                      title="View details"
                    >
                      <SquareStack className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setHistoryRow(row)}
                      className="rounded p-1 text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-gray-900"
                      title="View change history"
                    >
                      <HistoryIcon className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingRow(row)}
                      className="rounded p-1 text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-primary-700"
                      title="Edit row"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(hasPrev || hasNext || submissions?.total !== undefined) && (
        <div className="flex items-center justify-between gap-2 border-t border-rgf-gray-200 px-3 py-2 text-xs">
          <div className="text-rgf-gray-500">
            {typeof submissions?.total === 'number'
              ? `${submissions.total.toLocaleString('en-IN')} total`
              : `Page ${page}`}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!hasPrev}
              className="rounded-md border border-rgf-gray-200 px-2 py-1 text-rgf-gray-600 hover:border-rgf-gray-300 hover:text-rgf-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNext}
              className="rounded-md border border-rgf-gray-200 px-2 py-1 text-rgf-gray-600 hover:border-rgf-gray-300 hover:text-rgf-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {meta && editingRow && (
        <ResourceEditDialog
          meta={meta}
          resource={childResource}
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSaved={refetchList}
        />
      )}

      {historyRow && (
        <ResourceHistoryDrawer
          resource={childResource}
          row={historyRow}
          onClose={() => setHistoryRow(null)}
          onRestored={refetchList}
        />
      )}

      {meta && detailRow && (
        <ResourceDetailDrawer
          meta={meta}
          parentResource={childResource}
          row={detailRow}
          onClose={() => setDetailRow(null)}
        />
      )}
    </div>
  );
}

function InlineNotice({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-rgf-gray-200 bg-white px-3 py-4 text-center text-xs text-rgf-gray-500">
      {message}
    </div>
  );
}

function ErrorNotice({ title, message }: { title: string; message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs">
      <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-600" />
      <div>
        <p className="font-medium text-red-900">{title}</p>
        <p className="mt-0.5 text-red-700">{message}</p>
      </div>
    </div>
  );
}

/**
 * Curated column set for the child grid — same logic as
 * `ResourceGrid.visibleColumns`. Prefers `summary_view` when the
 * child meta declares one so tables like `intervention_adoption`
 * (many fields) render at readable width; falls back to
 * `list_visible !== false` for children that skipped summary_view.
 */
function visibleColumns(meta: ResourceMeta): FieldMeta[] {
  const fields = meta.view_meta?.fields ?? [];
  const summaryView = meta.view_meta?.summary_view;

  if (summaryView) {
    const orderedTitles: string[] = [];
    const push = (t: string | undefined | null) => {
      if (typeof t !== 'string' || t === '') return;
      if (orderedTitles.includes(t)) return;
      orderedTitles.push(t);
    };
    push(summaryView.id);
    push(summaryView.title);
    push(summaryView.subtitle);
    for (const s of summaryView.summary ?? []) push(s);
    if (orderedTitles.length > 0) {
      const byTitle = new Map(fields.map((f) => [f.title, f]));
      return orderedTitles
        .map((t) => byTitle.get(t))
        .filter((f): f is FieldMeta => Boolean(f));
    }
  }
  return fields.filter((f) => f.list_visible !== false);
}

function sortableColumnTitles(meta: ResourceMeta): Set<string> {
  return new Set(meta.view_meta?.sort?.columns ?? []);
}
